import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const RecentActivity = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);

  const activities = [
    {
      id: 1,
      type: 'order',
      title: 'New order received',
      description: 'Order #ORD-2024-1156 from Sarah Johnson',
      timestamp: new Date(Date.now() - 300000),
      icon: 'ShoppingBag',
      color: 'text-success',
      amount: '$89.99'
    },
    {
      id: 2,
      type: 'product',
      title: 'Product added',
      description: 'Luxury Hydrating Serum by GlowBeauty',
      timestamp: new Date(Date.now() - 900000),
      icon: 'Package',
      color: 'text-primary',
      amount: null
    },
    {
      id: 3,
      type: 'user',
      title: 'New customer registration',
      description: 'Emma Wilson joined Bounderlaster',
      timestamp: new Date(Date.now() - 1800000),
      icon: 'UserPlus',
      color: 'text-accent-foreground',
      amount: null
    },
    {
      id: 4,
      type: 'vendor',
      title: 'Vendor approval request',
      description: 'PureSkin Cosmetics submitted application',
      timestamp: new Date(Date.now() - 3600000),
      icon: 'Building2',
      color: 'text-warning',
      amount: null
    },
    {
      id: 5,
      type: 'payment',
      title: 'Payment processed',
      description: 'Order #ORD-2024-1155 payment confirmed',
      timestamp: new Date(Date.now() - 5400000),
      icon: 'CreditCard',
      color: 'text-success',
      amount: '$156.50'
    },
    {
      id: 6,
      type: 'inventory',
      title: 'Low stock alert',
      description: 'Matte Liquid Lipstick - Ruby Red (5 units left)',
      timestamp: new Date(Date.now() - 7200000),
      icon: 'AlertTriangle',
      color: 'text-error',
      amount: null
    }
  ];

  const filters = [
    { value: 'all', label: 'All Activity', icon: 'Activity' },
    { value: 'order', label: 'Orders', icon: 'ShoppingBag' },
    { value: 'product', label: 'Products', icon: 'Package' },
    { value: 'user', label: 'Users', icon: 'Users' }
  ];

  const handleActivityClick = (activity) => {
    if (activity?.type === 'vendor') {
      navigate('/vendor-management');
    }
    // Add other navigation logic for different activity types if needed
  };

  const getActivityConfig = (type) => {
    const configs = {
      order: { icon: 'ShoppingBag', iconColor: 'text-success', bgColor: 'bg-success/10' },
      product: { icon: 'Package', iconColor: 'text-primary', bgColor: 'bg-primary/10' },
      user: { icon: 'UserPlus', iconColor: 'text-accent-foreground', bgColor: 'bg-accent-foreground/10' },
      vendor: { icon: 'Building2', iconColor: 'text-warning', bgColor: 'bg-warning/10' },
      payment: { icon: 'CreditCard', iconColor: 'text-success', bgColor: 'bg-success/10' },
      inventory: { icon: 'AlertTriangle', iconColor: 'text-error', bgColor: 'bg-error/10' }
    };
    return configs?.[type] || configs?.order;
  };

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities?.filter(activity => activity?.type === filter);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp?.toLocaleDateString();
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-elevation-1">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground">Recent Activity</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Latest system events and updates
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {filteredActivities?.slice(0, showAll ? undefined : 5)?.map((activity, index) => {
          const config = getActivityConfig(activity?.type);
          return (
            <div
              key={index}
              onClick={() => handleActivityClick(activity)}
              className={`flex items-start space-x-3 p-3 bg-muted/30 rounded-lg transition-colors ${
                activity?.type === 'vendor' 
                  ? 'hover:bg-primary/10 cursor-pointer' :'hover:bg-muted/50'
              }`}
            >
              <div className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center ${config?.bgColor}`}>
                <Icon name={config?.icon} size={16} className={config?.iconColor} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground break-words leading-relaxed">
                      {activity?.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 break-words leading-relaxed">
                      {activity?.description}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {formatTimeAgo(activity?.timestamp)}
                  </span>
                </div>
                
                {activity?.metadata && (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {Object.entries(activity?.metadata)?.map(([key, value]) => (
                      <span 
                        key={key}
                        className="text-xs bg-background px-2 py-1 rounded border border-border"
                      >
                        <span className="font-medium capitalize">{key}:</span> {value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {activities?.length > 5 && (
        <div className="mt-6 pt-4 border-t border-border">
          <Button
            variant="outline"
            fullWidth
            iconName={showAll ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Less' : `Show All ${activities?.length} Activities`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;